import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Loader2, Mail, Trash2, X, Shield, UserCheck, UserX, KeyRound, Send, RefreshCw,
} from 'lucide-react';
import {
  listAllUsersAdmin as listUsers, createUser, updateUser, deleteUser, resendInvite, adminResetUserPassword,
  listDepartments, listJobTitles,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import PermissionsDialog from '../../components/PermissionsDialog';

const ROLE_META = {
  super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-700', desc: 'Full access — including user management' },
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700', desc: 'Full CMS + CRM access, cannot manage users' },
  content_publisher: { label: 'Content Publisher', color: 'bg-blue-100 text-blue-700', desc: 'CMS only — news, verticals, personnel, home layout' },
  staff: { label: 'Staff', color: 'bg-emerald-100 text-emerald-700', desc: 'CRM only — tickets, tasks, messages' },
};

const UsersAdminPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [permsFor, setPermsFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, d, j] = await Promise.all([
        listUsers(),
        listDepartments().catch(() => ({ data: [] })),
        listJobTitles().catch(() => ({ data: [] })),
      ]);
      setUsers(u.data || []);
      setDepts(d.data || []);
      setJobTitles(j.data || []);
    } catch { toast({ title: 'Failed to load users', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Re-open the invite modal automatically if the user was mid-invite
  // and jumped away to add a department / job title.
  useEffect(() => {
    if (window.sessionStorage.getItem('invite_reopen') === '1') {
      window.sessionStorage.removeItem('invite_reopen');
      setShowInvite(true);
    }
  }, []);

  const patch = async (userId, fields) => {
    try {
      await updateUser(userId, fields);
      toast({ title: 'User updated' });
      load();
    } catch (err) { toast({ title: 'Update failed', variant: 'destructive', description: err?.response?.data?.detail || '' }); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})?`)) return;
    try { await deleteUser(u.user_id); load(); }
    catch (err) { toast({ title: 'Delete failed', variant: 'destructive', description: err?.response?.data?.detail || '' }); }
  };
  const resend = async (u) => {
    if (!window.confirm(`Resend invite email to ${u.email}?\n\nThis will send a fresh 48-hour set-password link.`)) return;
    try {
      await resendInvite(u.user_id);
      toast({ title: 'Invite resent', description: `Fresh invite sent to ${u.email}` });
    } catch (err) {
      toast({ title: 'Resend failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    }
  };
  const reset = async (u) => {
    if (!window.confirm(`Email a reset-password link to ${u.email}?`)) return;
    try {
      await adminResetUserPassword(u.user_id);
      toast({ title: 'Reset link emailed', description: `Sent to ${u.email}` });
    } catch (err) {
      toast({ title: 'Reset failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Access</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Users & Roles
          </h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">
            Invite team members, assign roles, and grant emergency permissions.
          </p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          data-testid="invite-user-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e]"
        >
          <Mail className="w-4 h-4" /> Invite user
        </button>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {Object.entries(ROLE_META).map(([k, r]) => (
          <div key={k} className="bg-white border border-black/5 rounded-xl p-3">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest ${r.color}`}>
              <Shield className="w-3 h-3" /> {r.label}
            </span>
            <p className="mt-2 text-xs text-[#0b1a2e]/60">{r.desc}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="bg-white border border-black/5 rounded-2xl p-10 text-center text-[#0b1a2e]/50">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
              <tr>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Department</th>
                <th className="text-left px-4 py-3">Job Title</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.user_id === currentUser?.user_id;
                const isJwt = u.source === 'jwt';
                return (
                  <tr key={u.user_id} data-testid="user-row" className="border-t border-black/5 hover:bg-[#f7fbf8]/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0b1a2e]">{u.name} {isSelf && <span className="text-xs text-[#0f5c3d]">(you)</span>}</div>
                      <div className="text-xs text-[#0b1a2e]/60">{u.email}</div>
                      <div className="text-[10px] text-[#0b1a2e]/40 mt-0.5 font-mono">{u.source === 'jwt' ? 'JWT · master' : 'Google sign-in'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        onChange={(e) => patch(u.user_id, { role: e.target.value })}
                        disabled={isSelf || isJwt}
                        data-testid="user-role-select"
                        className="border border-[#0f5c3d]/15 rounded px-2 py-1 text-xs bg-white disabled:opacity-60"
                      >
                        {Object.entries(ROLE_META).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.department_id || ''}
                        onChange={(e) => patch(u.user_id, { department_id: e.target.value || null })}
                        className="border border-[#0f5c3d]/15 rounded px-2 py-1 text-xs bg-white"
                      >
                        <option value="">— none —</option>
                        {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.job_title_id || ''}
                        onChange={(e) => patch(u.user_id, { job_title_id: e.target.value || null })}
                        className="border border-[#0f5c3d]/15 rounded px-2 py-1 text-xs bg-white"
                      >
                        <option value="">— none —</option>
                        {jobTitles.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => patch(u.user_id, { active: !u.active })}
                        disabled={isSelf || isJwt}
                        className={`inline-flex items-center gap-1 text-xs font-medium ${u.active !== false ? 'text-emerald-700' : 'text-red-600'} ${isSelf || isJwt ? 'opacity-60' : ''}`}
                      >
                        {u.active !== false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        {u.active !== false ? 'Active' : 'Suspended'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {!isJwt && (
                        <button
                          onClick={() => resend(u)}
                          title="Resend invite email with fresh 48h set-password link"
                          data-testid="user-resend-invite-btn"
                          className="p-1.5 rounded-lg text-[#0f5c3d] hover:bg-[#f7fbf8]"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      {!isJwt && (
                        <button
                          onClick={() => reset(u)}
                          title="Email a reset-password link to this user"
                          data-testid="user-reset-password-btn"
                          className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setPermsFor(u)}
                        title="Grant custom permissions"
                        data-testid="user-permissions-btn"
                        className="p-1.5 rounded-lg text-[#0b1a2e]/70 hover:bg-black/5"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      {!isSelf && !isJwt && (
                        <button onClick={() => remove(u)} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && (
        <InviteModal
          depts={depts}
          jobTitles={jobTitles}
          onClose={() => setShowInvite(false)}
          onInvited={() => { setShowInvite(false); load(); }}
        />
      )}

      {permsFor && (
        <PermissionsDialog
          user={permsFor}
          onClose={(changed) => { setPermsFor(null); if (changed) load(); }}
        />
      )}
    </div>
  );
};

const InviteModal = ({ depts, jobTitles, onClose, onInvited }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', name: '', role: 'staff', department_id: '', job_title_id: '', send_invite: true });
  const [saving, setSaving] = useState(false);

  // Persist draft so we can restore it after visiting a Master and returning
  useEffect(() => {
    const raw = window.sessionStorage.getItem('invite_draft');
    if (raw) {
      try { setForm(JSON.parse(raw)); } catch { /* ignore */ }
      window.sessionStorage.removeItem('invite_draft');
    }
  }, []);

  const goToMaster = (tab) => {
    window.sessionStorage.setItem('invite_draft', JSON.stringify(form));
    window.sessionStorage.setItem('invite_reopen', '1');
    navigate(`/admin/masters?tab=${tab}&returnTo=/admin/users`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createUser({
        ...form,
        department_id: form.department_id || null,
        job_title_id: form.job_title_id || null,
      });
      toast({ title: 'User invited', description: form.send_invite ? `Email sent to ${form.email}` : 'Account created' });
      onInvited();
    } catch (err) {
      toast({ title: 'Invite failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">Invite</div>
            <div className="text-lg font-semibold text-[#0b1a2e]">New team member</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Full name</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Email (must match their Google account)</span>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Role</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm bg-white">
              {Object.entries(ROLE_META).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
            </select>
            <p className="mt-1 text-[11px] text-[#0b1a2e]/60">{ROLE_META[form.role]?.desc}</p>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70">Department</span>
                <button
                  type="button"
                  onClick={() => goToMaster('departments')}
                  data-testid="invite-add-dept-btn"
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-[#0f5c3d] hover:underline"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              <select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} data-testid="invite-dept" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— none —</option>
                {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70">Job Title</span>
                <button
                  type="button"
                  onClick={() => goToMaster('job-titles')}
                  data-testid="invite-add-jt-btn"
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-[#0f5c3d] hover:underline"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              <select value={form.job_title_id} onChange={(e) => setForm({ ...form, job_title_id: e.target.value })} data-testid="invite-jt" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— none —</option>
                {jobTitles.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} className="accent-[#0f5c3d]" data-testid="invite-send-email" />
            Send invitation email
          </label>
        </div>
        <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
          <button type="submit" disabled={saving} data-testid="submit-invite" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send invite
          </button>
        </div>
      </form>
    </div>
  );
};

export default UsersAdminPage;
